import { showToast } from "../utils/toast";

export interface PipelineStageData {
  id: string;
  name: string;
  color: string;
  kind: string;
}

export interface PipelineReasonData {
  id: string;
  label: string;
}

export interface PipelinePageData {
  stages: PipelineStageData[];
  reasons: PipelineReasonData[];
  stages_by_bot?: Record<string, PipelineStageData[]>;
  reasons_by_bot?: Record<string, PipelineReasonData[]>;
  filters: Record<string, string | string[]>;
}

export interface PipelineResponse {
  ok?: boolean;
  errors?: string[];
  view_html?: string;
  drawer_html?: string | null;
  count?: number;
  done?: number;
  skipped?: { id: string; why: string }[];
}

type Listener = (root: HTMLElement) => void;
const viewListeners: Listener[] = [];

export const onViewReplaced = (listener: Listener): void => {
  viewListeners.push(listener);
};

export const csrf = (): string =>
  document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content || "";

export const pipelineRoot = (): HTMLElement | null => document.querySelector<HTMLElement>("[data-pl-root]");

export function pageData(root: HTMLElement | null = pipelineRoot()): PipelinePageData {
  const raw = root?.querySelector<HTMLScriptElement>("script[data-pl-data]")?.textContent;
  try {
    return raw ? (JSON.parse(raw) as PipelinePageData) : { stages: [], reasons: [], filters: {} };
  } catch {
    return { stages: [], reasons: [], filters: {} };
  }
}

export async function sendJson(url: string, method: string, body: Record<string, unknown>): Promise<PipelineResponse> {
  const root = pipelineRoot();
  const payload = { ...body, filters: pageData(root).filters, context: root?.dataset.context || "bot" };
  try {
    const response = await fetch(url, {
      method,
      credentials: "same-origin",
      headers: { "X-CSRF-Token": csrf(), Accept: "application/json", "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    const json = (await response.json().catch(() => ({}))) as PipelineResponse;
    if (!response.ok) return { ok: false, errors: json.errors?.length ? json.errors : [failureMessage(response.status)] };
    return json;
  } catch {
    return { ok: false, errors: ["Couldn't reach the server. Try again."] };
  }
}

function failureMessage(status: number): string {
  if (status === 403) return "You can't change this pipeline.";
  if (status === 404) return "This candidate is no longer in the pipeline.";
  return "Something went wrong. Try again.";
}

export function applyResponse(result: PipelineResponse): boolean {
  if (!result.ok) {
    showToast(result.errors?.join(" ") || "Something went wrong. Try again.", "error", 3200);
    return false;
  }
  const root = pipelineRoot();
  const view = root?.querySelector<HTMLElement>("[data-pl-view]");
  if (root && view && typeof result.view_html === "string") {
    view.innerHTML = result.view_html;
    viewListeners.forEach((listener) => listener(root));
  }
  if (typeof result.count === "number") {
    const text = result.count.toLocaleString("en-US");
    document.querySelectorAll<HTMLElement>("[data-pl-count], [data-pl-tab-count]").forEach((el) => (el.textContent = text));
  }
  const drawer = root?.querySelector<HTMLElement>("[data-pl-drawer].open");
  if (drawer && typeof result.drawer_html === "string") drawer.innerHTML = result.drawer_html;
  if (result.skipped?.length) {
    showToast(`${result.done || 0} updated · ${result.skipped.length} skipped`, "success", 2600);
  }
  return true;
}

export async function updateEntry(entryId: string, body: Record<string, unknown>): Promise<PipelineResponse> {
  const root = pipelineRoot();
  const template = root?.dataset.entryUrl || "/pipeline_entries/__ID__";
  return sendJson(template.replace("__ID__", encodeURIComponent(entryId)), "PATCH", body);
}
